<template>
  <v-app>
    <v-container>
      <v-row>
        <v-col cols="12">
          <v-card class="mb-4">
            <v-card-title class="headline">
              My Profile
              <v-spacer></v-spacer>
              <v-btn color="grey" dark @click="goBack">
                <v-icon left class="material-icons-round">arrow_left</v-icon>
                Back
              </v-btn>
            </v-card-title>

            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <v-card outlined>
                    <v-card-title>
                      Personal Information
                      <v-spacer></v-spacer>
                      <v-btn
                        small
                        color="blue"
                        text
                        @click="editPersonalInfo = true"
                      >
                        <v-icon small left class="material-icons-round">edit</v-icon>
                        Edit
                      </v-btn>
                    </v-card-title>

                    <v-card-text>
                      <v-list-item>
                        <v-list-item-icon>
                          <v-icon class="material-icons-round">person</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>Name</v-list-item-title>
                          <v-list-item-subtitle>{{ user.first_name }} {{ user.last_name }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item>
                        <v-list-item-icon>
                          <v-icon class="material-icons-round">email</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>Email</v-list-item-title>
                          <v-list-item-subtitle>{{ user.email }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item>
                        <v-list-item-icon>
                          <v-icon class="material-icons-round">phone</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>Phone</v-list-item-title>
                          <v-list-item-subtitle>{{ user.phone_m || 'Not provided' }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12" md="6">
                  <v-card outlined>
                    <v-card-title>
                      <v-icon left color="primary" class="material-icons-round">lock</v-icon>
                      Security
                    </v-card-title>

                    <v-card-text>
                      <v-btn
                        block
                        color="orange"
                        dark
                        @click="changePasswordDialog = true"
                        class="mb-4"
                      >
                        <v-icon left class="material-icons-round">lock_reset</v-icon>
                        Change Password
                      </v-btn>

                      <v-btn
                        block
                        color="red"
                        dark
                        @click="logout"
                      >
                        <v-icon left class="material-icons-round">logout</v-icon>
                        Logout
                      </v-btn>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12">
                  <v-card outlined>
                    <v-card-title>
                      Booking Summary
                    </v-card-title>

                    <v-card-text>
                      <v-row>
                        <v-col cols="12" sm="4">
                          <v-card class="text-center" color="light-blue" dark>
                            <v-card-text>
                              <div class="text-h4">{{ bookingSummary.total || 0 }}</div>
                              <div>Total Bookings</div>
                            </v-card-text>
                          </v-card>
                        </v-col>

                        <v-col cols="12" sm="4">
                          <v-card class="text-center" color="green" dark>
                            <v-card-text>
                              <div class="text-h4">{{ bookingSummary.active || 0 }}</div>
                              <div>Active Bookings</div>
                            </v-card-text>
                          </v-card>
                        </v-col>

                        <v-col cols="12" sm="4">
                          <v-card class="text-center" color="grey" dark>
                            <v-card-text>
                              <div class="text-h4">${{ bookingSummary.totalSpent || 0 }}</div>
                              <div>Total Spent</div>
                            </v-card-text>
                          </v-card>
                        </v-col>
                      </v-row>

                      <v-btn
                        block
                        color="blue"
                        dark
                        class="mt-4"
                        @click="goToBookings"
                      >
                        View All Bookings
                      </v-btn>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Edit Personal Info Dialog -->
      <v-dialog v-model="editPersonalInfo" max-width="500px">
        <v-card>
          <v-card-title>
            Edit Personal Information
            <v-spacer></v-spacer>
            <v-btn icon @click="editPersonalInfo = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-form ref="editForm" v-model="validEditForm">
              <v-text-field
                v-model="editedUser.first_name"
                label="First Name"
                :rules="[v => !!v || 'First name is required']"
                required
              ></v-text-field>

              <v-text-field
                v-model="editedUser.last_name"
                label="Last Name"
                :rules="[v => !!v || 'Last name is required']"
                required
              ></v-text-field>

              <v-text-field
                v-model="editedUser.phone_m"
                label="Phone"
                :rules="[v => !v || /^\d{10}$/.test(v.replace(/\D/g, '')) || 'Phone number must be 10 digits']"
                v-mask="'(###) ###-####'"
              ></v-text-field>
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="error" text @click="editPersonalInfo = false">Cancel</v-btn>
            <v-btn
              color="primary"
              :loading="updateLoading"
              :disabled="!validEditForm || updateLoading"
              @click="updateProfile"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Change Password Dialog -->
      <v-dialog v-model="changePasswordDialog" max-width="500px">
        <v-card>
          <v-card-title>
            Change Password
            <v-spacer></v-spacer>
            <v-btn icon @click="changePasswordDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-form ref="passwordForm" v-model="validPasswordForm">
              <v-text-field
                v-model="passwordData.current_password"
                label="Current Password"
                type="password"
                :rules="[v => !!v || 'Current password is required']"
                required
              ></v-text-field>

              <v-text-field
                v-model="passwordData.new_password"
                label="New Password"
                type="password"
                :rules="[
                  v => !!v || 'New password is required',
                  v => v.length >= 8 || 'Password must be at least 8 characters'
                ]"
                required
              ></v-text-field>

              <v-text-field
                v-model="passwordData.confirm_password"
                label="Confirm New Password"
                type="password"
                :rules="[
                  v => !!v || 'Please confirm your password',
                  v => v === passwordData.new_password || 'Passwords do not match'
                ]"
                required
              ></v-text-field>
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="error" text @click="changePasswordDialog = false">Cancel</v-btn>
            <v-btn
              color="primary"
              :loading="passwordLoading"
              :disabled="!validPasswordForm || passwordLoading"
              @click="changePassword"
            >
              Update Password
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-app>
</template>

<script>
export default {
  name: 'RentalProfile',

  data() {
    return {
      user: {
        first_name: '',
        last_name: '',
        email: '',
        phone_m: ''
      },
      bookingSummary: {
        total: 0,
        active: 0,
        totalSpent: 0
      },
      editPersonalInfo: false,
      editedUser: {
        first_name: '',
        last_name: '',
        phone_m: ''
      },
      validEditForm: false,
      updateLoading: false,
      changePasswordDialog: false,
      passwordData: {
        current_password: '',
        new_password: '',
        confirm_password: ''
      },
      validPasswordForm: false,
      passwordLoading: false
    };
  },

  created() {
    this.checkAuth();
    this.loadUserData();
    this.fetchBookingSummary();
  },

  methods: {
    checkAuth() {
      // Check if user is logged in
      const token = localStorage.getItem('rental_token');
      if (!token) {
        // Redirect to login page if not logged in
        this.$router.push('/rental/login');
      }
    },

    loadUserData() {
      // Load user data from localStorage
      const userData = JSON.parse(localStorage.getItem('rental_user'));
      if (userData) {
        this.user = userData;
        // Initialize edited user data
        this.editedUser = {
          first_name: userData.first_name,
          last_name: userData.last_name,
          phone_m: userData.phone_m || ''
        };
      }
    },

    fetchBookingSummary() {
      // Get token from localStorage
      const token = localStorage.getItem('rental_token');

      // Fetch booking summary from API
      window.axios.get('/api/auth/rental/booking-summary', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => {
        if (response.data.flag === 1) {
          this.bookingSummary = response.data.data;
          console.log('Booking summary loaded:', this.bookingSummary);
        } else {
          console.error('Failed to load booking summary:', response.data.message);
        }
      })
      .catch(error => {
        console.error('Error fetching booking summary:', error);
        console.error('Error details:', error.response ? error.response.data : 'No response data');
        console.error('Error status:', error.response ? error.response.status : 'No status');

        // Set default values in case of error
        this.bookingSummary = {
          total: 0,
          active: 0,
          totalSpent: 0
        };

        // Show error toast
        this.$toast.error(`Failed to load booking summary: ${error.response ? error.response.status : 'Network error'}`, {
          position: 'top-right',
          duration: 5000
        });
      });
    },

    updateProfile() {
      if (!this.$refs.editForm.validate()) return;

      this.updateLoading = true;

      // Get token from localStorage
      const token = localStorage.getItem('rental_token');

      // Update profile via API
      window.axios.post('/api/rental/update-profile', this.editedUser, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => {
        if (response.data.flag === 1) {
          // Update local user data
          this.user = {
            ...this.user,
            first_name: this.editedUser.first_name,
            last_name: this.editedUser.last_name,
            phone_m: this.editedUser.phone_m
          };

          // Update localStorage
          localStorage.setItem('rental_user', JSON.stringify(this.user));

          // Show success message
          this.$toast.success('Profile updated successfully', {
            position: 'top-right'
          });

          // Close dialog
          this.editPersonalInfo = false;
        } else {
          this.$toast.error(response.data.message || 'Failed to update profile', {
            position: 'top-right'
          });
        }
      })
      .catch(error => {
        console.error('Error updating profile:', error);
        this.$toast.error('An error occurred while updating your profile', {
          position: 'top-right'
        });
      })
      .finally(() => {
        this.updateLoading = false;
      });
    },

    changePassword() {
      if (!this.$refs.passwordForm.validate()) return;

      this.passwordLoading = true;

      // Get token from localStorage
      const token = localStorage.getItem('rental_token');

      // Change password via API
      window.axios.post('/api/rental/change-password', {
        current_password: this.passwordData.current_password,
        new_password: this.passwordData.new_password
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => {
        if (response.data.flag === 1) {
          // Show success message
          this.$toast.success('Password changed successfully', {
            position: 'top-right'
          });

          // Reset form and close dialog
          this.passwordData = {
            current_password: '',
            new_password: '',
            confirm_password: ''
          };
          this.changePasswordDialog = false;
        } else {
          this.$toast.error(response.data.message || 'Failed to change password', {
            position: 'top-right'
          });
        }
      })
      .catch(error => {
        console.error('Error changing password:', error);
        this.$toast.error(error.response?.data?.message || 'An error occurred while changing your password', {
          position: 'top-right'
        });
      })
      .finally(() => {
        this.passwordLoading = false;
      });
    },

    logout() {
      // Clear user data from localStorage
      localStorage.removeItem('rental_token');
      localStorage.removeItem('rental_user');

      // Show success message
      this.$toast.success('Logged out successfully', {
        position: 'top-right'
      });

      // Redirect to home page
      this.$router.push('/');
    },

    goToBookings() {
      this.$router.push('/rental/bookings');
    },

    goBack() {
      this.$router.go(-1);
    }
  }
};
</script>

<style scoped>
.headline {
  display: flex;
  align-items: center;
}
</style>
